import React, { createContext, useContext, useReducer, useEffect } from 'react'
import authService from '../services/auth'

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
}

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  CLEAR_ERROR: 'CLEAR_ERROR'
}

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null
      }
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      }
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      }
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      }
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      }
    
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false
      }
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      }
    
    default:
      return state
  }
}

// Create context
const AuthContext = createContext()

// Provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = authService.getStoredToken()
        const user = authService.getStoredUser()
        
        // Check if this is an OAuth callback
        const isOAuthCallback = window.location.search.includes('oauth_success=true') || window.location.search.includes('oauth_error=')
        
        if (token && user) {
          // For OAuth callbacks, trust the stored token temporarily
          if (isOAuthCallback) {
            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: { user, token }
            })
            
            // Verify token in background
            setTimeout(async () => {
              try {
                const currentUser = await authService.getCurrentUser()
                dispatch({
                  type: AUTH_ACTIONS.SET_USER,
                  payload: currentUser
                })
              } catch (error) {
                console.error('Background auth verification error:', error)
                // Only logout if it's not a temporary network issue
                if (error.status === 401 || error.status === 403) {
                  await authService.logout()
                  dispatch({ type: AUTH_ACTIONS.LOGOUT })
                }
              }
            }, 500)
          } else {
            // Normal initialization - verify token immediately
            try {
              const currentUser = await authService.getCurrentUser()
              dispatch({
                type: AUTH_ACTIONS.LOGIN_SUCCESS,
                payload: { user: currentUser, token }
              })
            } catch (error) {
              console.error('Auth initialization error:', error)
              
              // Check if it's an email verification error (403)
              if (error.status === 403 && error.message?.includes('Email verification required')) {
                // Keep the user logged in but mark as unverified
                const unverifiedUser = { ...user, email_verified: false }
                dispatch({
                  type: AUTH_ACTIONS.LOGIN_SUCCESS,
                  payload: { user: unverifiedUser, token }
                })
              } else {
                // Token is invalid, clear storage and set loading to false
                await authService.logout()
                dispatch({ type: AUTH_ACTIONS.LOGOUT })
              }
            }
          }
        } else {
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
      }
    }

    initializeAuth()
  }, [])

  // Login function
  const login = async (email, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START })
    
    try {
      const result = await authService.login(email, password)
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: result
      })
      return result
    } catch (error) {
      const errorMessage = error.message || error.detail || 'Login failed'
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage
      })
      throw error
    }
  }

  // Register function - Don't auto-login, user needs to verify email first
  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START })
    
    try {
      const result = await authService.register(userData)
      // Delay the loading state reset to allow success callback to execute first
      setTimeout(() => {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
      }, 100)
      return result
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: error.message
      })
      throw error
    }
  }

  // Logout function
  const logout = async () => {
    console.log('AuthContext logout called')
    try {
      console.log('Calling authService.logout()...')
      await authService.logout()
      console.log('authService.logout() completed')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      console.log('Dispatching LOGOUT action')
      dispatch({ type: AUTH_ACTIONS.LOGOUT })
    }
  }

  // Update user function
  const updateUser = (userData) => {
    dispatch({
      type: AUTH_ACTIONS.SET_USER,
      payload: userData
    })
  }

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR })
  }

  // Google OAuth login
  const loginWithGoogle = () => {
    window.location.href = authService.getGoogleAuthUrl()
  }

  // Handle OAuth callback
  const handleOAuthCallback = async (code, state) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START })
    
    try {
      const result = await authService.handleOAuthCallback(code, state)
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: result
      })
      return result
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: error.message
      })
      throw error
    }
  }

  const value = {
    ...state,
    login,
    register,
    logout,
    updateUser,
    clearError,
    loginWithGoogle,
    handleOAuthCallback
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext